import { isDemoMode } from '@/storage/demoMode';
import { getApiBaseUrl } from '@/app/runtime';
import i18n from '@/i18n';
import { getActiveShareSessionToken } from '@/features/custom-dashboards/api/SharedDashboardsRepository';
import { showToast } from '@/plugins/toast';
import { signalFeedbackIssue } from '@/composables/useFeedback';
import { useEntitlementsStore } from '@/stores/useEntitlementsStore';
import axios from 'axios';
import normalizeBaseDomain from './normalizeBaseDomain';
import {
  isOnLoginRoute,
  isOnPublicRoute,
  redirectToLoginOnSessionExpired,
  refreshAccessToken,
} from './sharedTokenRefresh';
import { notifyInsufficientTokens } from './tokenErrors';

const baseDomain = normalizeBaseDomain(getApiBaseUrl());
const baseURL = baseDomain;

const request = axios.create({
  baseURL,
});

request.interceptors.request.use(
  (config) => {
    // Add Scrumble JWT token from session storage for authentication
    const accessToken = window.sessionStorage.getItem('accessToken');
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    if (!accessToken && isDemoMode()) config.headers['X-Portal-Demo'] = '1';

    // Shared dashboard session — inject X-Share-Session when active
    const shareSession = getActiveShareSessionToken();
    if (shareSession) {
      config.headers['X-Share-Session'] = shareSession;
    }

    config.headers['Accept-Language'] = i18n.global.locale.value;
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Notify the unified entitlements store when the backend reports a different
 * `X-Entitlements-Version` than we have cached, so it can refetch
 * /api/v2/me/access.
 *
 * The Pinia store is resolved lazily inside the interceptor (not at module
 * load) because `setActivePinia()` runs in src/main.js before any request
 * fires — calling `useEntitlementsStore()` at module evaluation time would
 * execute before Pinia is active. Guarded so a misconfigured boot (no active
 * Pinia) degrades to a no-op instead of throwing inside this hot path.
 */
function syncEntitlementsVersion(headerValue) {
  if (!headerValue) {
    return;
  }
  try {
    useEntitlementsStore().syncAccessVersion(headerValue);
  } catch {
    // No active Pinia (e.g. very early boot or a non-app context) — skip.
  }
}

/**
 * Dispatch a custom event when the backend returns
 * `403 entitlement_missing` so the global UpgradeModal can react.
 * The payload carries `missing_keys` from the backend dependency.
 */
function notifyEntitlementMissing(detail) {
  if (typeof window === 'undefined') {
    return;
  }
  window.dispatchEvent(new CustomEvent('entitlement:missing', { detail }));
}

request.interceptors.response.use(
  (response) => {
    syncEntitlementsVersion(response?.headers?.['x-entitlements-version']);
    return response;
  },
  async (error) => {
    const { config, response } = error;
    const status = response?.status ?? 200;

    syncEntitlementsVersion(response?.headers?.['x-entitlements-version']);

    // Server errors and outright network failures are what make charts, tools
    // and benchmarks fall into an error state. Surface the subtle feedback nudge
    // (dismissible, auto-hiding) so the user can report it. Expected 4xx (auth,
    // validation, entitlement) and cancelled requests are not "bugs" — skip them.
    if (!axios.isCancel(error) && (!response || status >= 500)) {
      signalFeedbackIssue({
        message: response ? `HTTP ${status}` : 'network error',
        route: config?.url || '',
      });
    }

    // 403 with structured entitlement_missing body — open the upgrade flow.
    // Phase 3e: detail.offer carries scrumble's resolved BillingOffer
    // (offer_type / stripe_price_id / name / self_serve). Surfaced as
    // event.detail.offer so the UpgradeModal can deep-link straight to
    // Stripe Checkout when self_serve, or to /subscriptions otherwise.
    //
    // Passive data/visualization loads opt out via `suppressEntitlementModal`
    // (set in DatasetRepository): those render a graceful per-card locked
    // state with its own upgrade link instead of a blocking global modal, so
    // a first-time user landing on a gated dashboard isn't ambushed on entry.
    if (
      status === 403 &&
      response?.data?.detail?.code === 'entitlement_missing' &&
      !config?.suppressEntitlementModal
    ) {
      notifyEntitlementMissing({
        missingKeys: response.data.detail.missing_keys ?? [],
        requiredAny: response.data.detail.required_any === true,
        offer: response.data.detail.offer ?? null,
        route: config?.url,
      });
    }

    notifyInsufficientTokens(response, config);

    // Only attempt token refresh for 401 errors on authenticated (non-share-session) requests.
    // Also skip if this request is itself a token refresh to avoid infinite loops.
    const isRefreshEndpoint = config.url?.includes('/auth/') && config.url?.includes('/refresh');
    if (
      status === 401 &&
      !config._retry &&
      !isRefreshEndpoint &&
      !config.headers['X-Share-Session']
    ) {
      // Don't even try to refresh when sitting on /login — the user is
      // already there, and a refresh failure would just reload the app
      // (window.location.href) which re-fires the very 401 we're handling,
      // re-fails the refresh, re-redirects… infinite loop.
      if (isOnLoginRoute()) {
        return Promise.reject(error);
      }
      config._retry = true;
      try {
        // SHARED refresh: every axios client in the app joins the same
        // in-flight refresh via `sharedTokenRefresh.js`. This eliminates
        // the cross-client race that used to invalidate the rotating
        // refresh token and trigger session_expired redirect loops.
        const accessToken = await refreshAccessToken({ rejectedAccessToken: config.headers.Authorization?.replace(/^Bearer /, '') });
        config.headers.Authorization = `Bearer ${accessToken}`;
        return request(config);
      } catch (err) {
        // `refreshAccessToken` already cleared the stale tokens (when it
        // had any). Bounce to /login only when we're not already there;
        // `redirectToLoginOnSessionExpired` enforces that guard centrally.
        if (!isOnPublicRoute()) {
          showToast({
            text: i18n.global.t('messages.session_expired'),
            tone: 'danger',
            durationMs: 4000,
          });
        }
        redirectToLoginOnSessionExpired();
        return Promise.reject(err);
      }
    }

    return Promise.reject(error);
  }
);

export default request;
